import { IDENTITY_PATHS, type IdentityPath, identityAuthUrl } from '@words-happen/app-urls';
import { AccountScreen, type AccountScreenProps, type AccountTab } from '@words-happen/screens';
import { useThemePreference } from '@words-happen/ui-components';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createProfileApi, type ProfileApi } from '../../api/profile';
import { thenPath } from '../../games/display-name-detour';
import { type BrowserLocation, windowLocation } from '../../navigation';
import { useProfile } from '../../profile';
import { ACCOUNT_TAB_ROUTES, ROUTES } from '../../routes';
import { useSession } from '../../session';
import { useAccountSettings } from '../../settings';

export interface AccountPageProps {
  /** The tab this route shows: /profile or /settings. */
  tab: AccountTab;
  /** The identity app's origin. Defaults to this build's environment. */
  identityOrigin?: string;
  /** The game app's origin, for the password reset's return_to. Defaults to this build's environment. */
  playOrigin?: string;
  /** How Create free account, Sign in and Reset password leave the app. Defaults to the window's location. */
  browserLocation?: BrowserLocation;
  /** Replaces words-happen-gql's profile API. */
  profileApi?: ProfileApi;
}

type ProfileProps = AccountScreenProps['profile'];
type SettingsProps = AccountScreenProps['settings'];

/** Loads the profile once the Profile tab has been shown, so opening /settings alone never asks Firebase. */
const useProfileTab = (tab: AccountTab, api: ProfileApi) => {
  const [profileShown, setProfileShown] = useState(tab === 'profile');
  useEffect(() => {
    if (tab === 'profile') setProfileShown(true);
  }, [tab]);

  return useProfile(api, { enabled: profileShown });
};

/**
 * The account area, /profile and /settings: one AccountScreen whose tabs are routes, so each is a deep link and back
 * and forward move between them. Choosing a tab pushes its route; the back control goes to the dashboard. The Profile
 * tab comes from words-happen-gql myProfile / updateDisplayName; the Settings tab from the app root's
 * WordsHappenThemeProvider (Theme, on this device) and AccountSettingsProvider (Keyboard layout under Gameplay and
 * Reduced motion under Account, both on the account). Guests are sent to the identity app to create an account or sign
 * in, with this page as return_to. With `?then=<path>` (`displayNameDetour`, when the lobby needs a display name),
 * saving a name goes on to that path.
 */
export const AccountPage: React.FC<AccountPageProps> = ({
  tab,
  identityOrigin = IDENTITY_URL,
  playOrigin = PLAY_URL,
  browserLocation = windowLocation,
  profileApi,
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const then = thenPath(search);
  const { signOut } = useSession();
  const { preference, setPreference } = useThemePreference();
  const accountSettings = useAccountSettings();
  const [api] = useState(() => profileApi ?? createProfileApi());
  const profileState = useProfileTab(tab, api);
  const [signingOut, setSigningOut] = useState(false);

  const goToIdentity = (path: IdentityPath) =>
    browserLocation.assign(identityAuthUrl(identityOrigin, path, { returnTo: browserLocation.href() }));
  const guest = {
    kind: 'guest',
    onCreateAccount: () => goToIdentity(IDENTITY_PATHS.register),
    onSignIn: () => goToIdentity(IDENTITY_PATHS.signIn),
  } as const;

  const { profile } = profileState;
  const profileProps: ProfileProps =
    profileState.status === 'guest'
      ? guest
      : {
          kind: 'player',
          status: profileState.status,
          onRetryLoad: profileState.retryLoad,
          displayName: profile?.displayName ?? null,
          suggestedDisplayName: profile?.suggestedDisplayName ?? null,
          email: profile?.email ?? null,
          resetPasswordHref: profile?.hasPassword
            ? identityAuthUrl(identityOrigin, IDENTITY_PATHS.forgotPassword, {
                returnTo: `${playOrigin}${ROUTES.profile}`,
              })
            : undefined,
          onSaveDisplayName: async (displayName) => {
            const failure = await profileState.saveDisplayName(displayName);
            if (failure === undefined && then) {
              navigate(then, { replace: true });
            }
            return failure;
          },
          onSignOut: () => {
            setSigningOut(true);
            void signOut();
          },
          signingOut,
        };

  const settingsProps: SettingsProps = {
    themePreference: preference,
    onThemePreferenceChange: setPreference,
    account:
      accountSettings.status === 'guest'
        ? guest
        : {
            kind: 'player',
            status: accountSettings.status,
            onRetryLoad: accountSettings.retryLoad,
            reducedMotion: accountSettings.settings.reducedMotion,
            onReducedMotionChange: (value) => accountSettings.update('reducedMotion', value),
            reducedMotionSaving: accountSettings.saving.has('reducedMotion'),
            reducedMotionSaveFailed: accountSettings.failed.reducedMotion !== undefined,
            onRetryReducedMotion: () => accountSettings.retry('reducedMotion'),
            keyboardLayout: accountSettings.settings.keyboardLayout,
            onKeyboardLayoutChange: (value) => accountSettings.update('keyboardLayout', value),
            keyboardLayoutSaving: accountSettings.saving.has('keyboardLayout'),
            keyboardLayoutSaveFailed: accountSettings.failed.keyboardLayout !== undefined,
            onRetryKeyboardLayout: () => accountSettings.retry('keyboardLayout'),
          },
  };

  return (
    <AccountScreen
      tab={tab}
      onTabChange={(next) => navigate(ACCOUNT_TAB_ROUTES[next])}
      onBack={() => navigate(ROUTES.dashboard)}
      profile={profileProps}
      settings={settingsProps}
    />
  );
};
